// pages/Vitrine/index.tsx
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ShoppingCart,
  Trash2,
  X,
  Send,
  ClipboardList,
  Plus,
  Check,
  MapPin,
} from "lucide-react";
import api from "../../services/api";
import "./Vitrine.css";

// fix dos ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PLACEHOLDER = "/placeholder-outdoor.png";

type PublicEquipment = {
  id: string;
  name: string;
  category: string;
  location: string;
  slots: number;
  photoUrl: string | null;
  latitude: number | null;
  longitude: number | null;
};

type Company = {
  id: string;
  name: string;
  phone: string | null;
  logoUrl: string | null;
};

// 🎨 Pin colorido (azul normal / verde selecionado)
const buildIcon = (selected: boolean) =>
  L.divIcon({
    className: "",
    html: `
      <div style="
        width:28px;height:28px;border-radius:50% 50% 50% 0;
        background:${selected ? "#16a34a" : "#2563eb"};transform:rotate(-45deg);
        border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);
        display:flex;align-items:center;justify-content:center;">
        <div style="width:9px;height:9px;background:#fff;border-radius:50%;transform:rotate(45deg);"></div>
      </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -26],
  });

// 🆕 Enquadra TODOS os pontos ao carregar
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;

    const isMobile = window.innerWidth <= 768;
    const paddingBottomRight: [number, number] = isMobile
      ? [40, window.innerHeight * 0.4]
      : [60, 60];
    const paddingTopLeft: [number, number] = isMobile ? [40, 90] : [60, 60];

    if (points.length === 1) {
      map.setView(points[0], 14);
      if (isMobile) {
        map.panBy([0, -(window.innerHeight * 0.2)], { animate: false });
      }
    } else {
      map.fitBounds(points, { paddingTopLeft, paddingBottomRight });
    }
  }, [points, map]);
  return null;
}

// 🆕 Voa até um ponto quando ele é focado
function FlyToSelected({ target }: { target: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;

    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      const targetPoint = map.project(target, 16);
      const offsetPoint = L.point(targetPoint.x, targetPoint.y + window.innerHeight * 0.2);
      const adjusted = map.unproject(offsetPoint, 16);
      map.flyTo(adjusted, 16, { duration: 1.2 });
    } else {
      map.flyTo(target, 16, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

export default function Vitrine() {
  const { companyId } = useParams();
  const [company, setCompany] = useState<Company | null>(null);
  const [equipments, setEquipments] = useState<PublicEquipment[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);

  useEffect(() => {
    api
      .get(`/public/vitrine/${companyId}/equipments`)
      .then(({ data }) => {
        setCompany(data.company);
        setEquipments(data.equipments);
      })
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [companyId]);

  // 🆕 toggle (seleciona/remove) + voa até o ponto
  function toggle(eq: PublicEquipment) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(eq.id) ? next.delete(eq.id) : next.add(eq.id);
      return next;
    });
    if (eq.latitude != null && eq.longitude != null) {
      setFlyTarget([eq.latitude, eq.longitude]);
    }
  }

  // 🆕 apenas aproxima/foca no ponto (SEM selecionar)
  function focusOn(eq: PublicEquipment) {
    if (eq.latitude != null && eq.longitude != null) {
      // pequeno truque pra "reativar" o flyTo mesmo no mesmo ponto
      setFlyTarget(null);
      requestAnimationFrame(() => setFlyTarget([eq.latitude!, eq.longitude!]));
    }
  }

  const selectedItems = useMemo(
    () => equipments.filter((e) => selected.has(e.id)),
    [equipments, selected]
  );

  const validEquipments = useMemo(
    () => equipments.filter((e) => e.latitude != null && e.longitude != null),
    [equipments]
  );

  const points = useMemo(
    () =>
      validEquipments.map((e) => [e.latitude!, e.longitude!] as [number, number]),
    [validEquipments]
  );

  function enviarWhatsApp() {
    if (!company?.phone || selectedItems.length === 0) return;

    let numero = company.phone.replace(/\D/g, "");
    if (!numero.startsWith("55")) numero = "55" + numero;

    const linhas = selectedItems
      .map((e, i) => `${i + 1}) ${e.name} — ${e.location} (${e.category})`)
      .join("\n");

    const msg =
      `Olá, ${company.name}! Tenho interesse em anunciar nos seguintes pontos:\n\n` +
      `${linhas}\n\nPodem me passar valores e disponibilidade?`;

    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(msg)}`, "_blank");
  }

// 🆕 link do WhatsApp da empresa (com mensagem padrão da vitrine)
const whatsappLink = useMemo(() => {
  if (!company?.phone) return null;
  let numero = company.phone.replace(/\D/g, "");
  if (!numero.startsWith("55")) numero = "55" + numero;

  const msg =
    `Olá, ${company.name}!\n\n` +
    `Vim através da sua vitrine de pontos e gostaria de mais informações sobre os espaços disponíveis para anúncio.`;

  return `https://wa.me/${numero}?text=${encodeURIComponent(msg)}`;
}, [company]);


  if (loading) return <div className="vitrine-loading">Carregando vitrine...</div>;
  if (!company) return <div className="vitrine-loading">Empresa não encontrada.</div>;

  const fallbackCenter: [number, number] = [-17.2, -46.8];

  return (
    <div className="vitrine">
      {/* MAPA FULLSCREEN */}
      <MapContainer
        center={points[0] ?? fallbackCenter}
        zoom={13}
        className="vitrine-map"
        zoomControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <FitBounds points={points} />
        <FlyToSelected target={flyTarget} />

        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={45}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
        >
          {validEquipments.map((eq) => (
            <Marker
              key={eq.id}
              position={[eq.latitude!, eq.longitude!]}
              icon={buildIcon(selected.has(eq.id))}
              eventHandlers={{ click: () => focusOn(eq) }} // 👈 só foca, não seleciona
            >
              <Popup>
                <div style={{ width: 200 }}>
                  <img
                    src={eq.photoUrl || PLACEHOLDER}
                    alt={eq.name}
                    style={{
                      width: "100%",
                      height: 110,
                      objectFit: "cover",
                      borderRadius: 8,
                      marginBottom: 6,
                    }}
                  />
                  <strong>{eq.name}</strong>
                  <br />
                  <small
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <MapPin size={12} /> {eq.location}
                  </small>

                  {/* 🆕 Botão de selecionar dentro do popup */}
                  <button
                    className={`popup-add ${selected.has(eq.id) ? "added" : ""}`}
                    onClick={() => toggle(eq)}
                  >
                    {selected.has(eq.id) ? (
                      <>
                        <Check size={16} /> Selecionado
                      </>
                    ) : (
                      <>
                        <Plus size={16} /> Adicionar ponto
                      </>
                    )}
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      {/* TOPBAR */}
      <div className="vitrine-topbar">
        {company.logoUrl && (
          <img src={company.logoUrl} alt={company.name} className="topbar-logo" />
        )}
        <h1>{company.name}</h1>

        {/* 🆕 Botão WhatsApp da empresa */}
        {whatsappLink && (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-whatsapp"
            title="Falar no WhatsApp"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span>{company.phone}</span>
          </a>
        )}
      </div>

      {/* LISTA LATERAL */}
      <aside className="vitrine-sidebar">
        <div className="sidebar-header">
          <h2>Pontos disponíveis</h2>
          <small>{equipments.length} pontos</small>
        </div>

        <div className="sidebar-list">
          {equipments.map((eq) => (
            <div
              key={eq.id}
              className={`ponto-card ${selected.has(eq.id) ? "ativo" : ""}`}
              onClick={() => focusOn(eq)} // 👈 só foca no mapa
            >
              <img
                src={eq.photoUrl || PLACEHOLDER}
                alt={eq.name}
                className="card-thumb"
              />
              <div className="card-info">
                <strong>{eq.name}</strong>
                <p>{eq.location}</p>
                <small>{eq.category}</small>
              </div>
              <button
                className={`card-add ${selected.has(eq.id) ? "added" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(eq); // 👈 só o + seleciona
                }}
              >
                {selected.has(eq.id) ? <Check size={18} /> : <Plus size={18} />}
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* BOTÃO FLUTUANTE DO CARRINHO */}
      {selectedItems.length > 0 && (
        <button className="cart-fab" onClick={() => setCartOpen(true)}>
          <ShoppingCart size={22} />
          <span className="cart-badge">{selectedItems.length}</span>
        </button>
      )}

      {/* PAINEL DESLIZANTE */}
      <div className={`cart-panel ${cartOpen ? "open" : ""}`}>
        <div className="cart-panel-header">
          <h2>
            <ClipboardList size={18} /> Pontos selecionados
          </h2>
          <button className="cart-close" onClick={() => setCartOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <div className="cart-panel-list">
          {selectedItems.length === 0 && (
            <p className="cart-empty">Nenhum ponto selecionado.</p>
          )}
          {selectedItems.map((eq) => (
            <div key={eq.id} className="cart-item">
              <img src={eq.photoUrl || PLACEHOLDER} alt={eq.name} />
              <div className="cart-item-info">
                <strong>{eq.name}</strong>
                <small>
                  {eq.category} • {eq.location}
                </small>
              </div>
              <button className="cart-remove" onClick={() => toggle(eq)}>
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>

        <div className="cart-panel-footer">
          <div className="cart-total">
            <span>Total de pontos</span>
            <strong>{selectedItems.length}</strong>
          </div>
          <button
            className="cart-send"
            disabled={selectedItems.length === 0}
            onClick={enviarWhatsApp}
          >
            <Send size={18} /> Enviar via WhatsApp
          </button>
        </div>
      </div>

      {/* OVERLAY */}
      {cartOpen && <div className="cart-overlay" onClick={() => setCartOpen(false)} />}
    </div>
  );
}
